/* Progressive motion: native scrolling, a single on-demand frame loop, no network. */
(() => {
  'use strict';
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const clamp = (n, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, n));
  const control = (key, fallback) => window.DIPN_CONFIG?.controls?.[key] ?? fallback;
  const intensity = () => Number(control('motion_strength',100))/100;
  const editing = Boolean(window.Shopify?.designMode);
  root.classList.toggle('theme-editor', editing);
  let paused = false;
  try { paused = localStorage.getItem('dipn-motion-paused') === 'true'; } catch {}
  let disabled = paused || reduced.matches || editing || !control('motion_enabled',true);
  let scene, panels = [], hero, editorial, visibility;
  let frame = 0, dirty = true, progress = 0, target = 0, headerHeight = 80;
  let activeCard = null, cardBounds = null, pointerX = 0, pointerY = 0, pointerDirty = false;

  function resetCard() {
    if (activeCard) {
      activeCard.style.removeProperty('--tilt-x');
      activeCard.style.removeProperty('--tilt-y');
    }
    activeCard = null;
    cardBounds = null;
    pointerDirty = false;
  }
  function syncControl() {
    document.querySelectorAll('[data-motion-toggle]').forEach(button => {
      const label = !control('motion_enabled',true) ? 'Motion off' : editing ? 'Editing preview' : reduced.matches ? 'Reduced motion' : disabled ? 'Play motion' : 'Pause motion';
      button.disabled = reduced.matches || editing || !control('motion_enabled',true);
      button.setAttribute('aria-label', label);
      button.setAttribute('title', reduced.matches ? 'Motion follows your device’s reduced motion setting' : label);
      button.setAttribute('aria-pressed', String(disabled));
      const text = button.querySelector('.motion-toggle-label');
      if (text) text.textContent = label;
    });
  }
  function applyPreference() {
    disabled = paused || reduced.matches || editing || !control('motion_enabled',true);
    root.classList.toggle('motion-enabled', !disabled);
    root.classList.toggle('motion-disabled', disabled);
    syncControl();
    if (disabled) {
      window.cancelAnimationFrame(frame);
      frame = 0;
      resetCard();
      hero?.style.removeProperty('--hero-parallax');
      editorial?.style.removeProperty('--editorial-parallax');
      scene?.style.removeProperty('--story-heading-y');
      scene?.style.removeProperty('--story-text-x');
    } else {
      dirty = true;
      requestFrame();
    }
  }
  function requestFrame() {
    if (!disabled && !document.hidden && !frame) frame = window.requestAnimationFrame(paint);
  }
  function measure() {
    dirty = false;
    const viewport = window.innerHeight;
    if (scene) {
      const rect = scene.getBoundingClientRect();
      target = viewport < 540 ? 1 : clamp((headerHeight - rect.top) / Math.max(1, rect.height - viewport + headerHeight));
    }
    if (hero && control('parallax_enabled',true)) {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < viewport) {
        hero.style.setProperty('--hero-parallax', clamp(-rect.top * .12 * intensity(), -40, 45).toFixed(2) + 'px');
      }
    }
    if (editorial && control('parallax_enabled',true)) {
      const rect = editorial.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < viewport) {
        const shift = clamp(-rect.height * .06 + (viewport / 2 - rect.top - rect.height / 2) * .08, -rect.height * .12, 0);
        editorial.style.setProperty('--editorial-parallax', (shift * intensity()).toFixed(2) + 'px');
      }
    }
  }
  function paint() {
    frame = 0;
    if (disabled || document.hidden) return;
    if (dirty) measure();
    progress += (target - progress) * .16;
    if (Math.abs(target - progress) < .0005) progress = target;
    if (scene) {
      // Complete the spread before the last fifth of the sticky scene.
      const spread = clamp(progress / .8);
      panels.forEach((panel, i) => {
        const side = i - 1;
        const x = side * (16 + 86 * spread);
        const mobileX = side * (14 + 62 * spread);
        const rotation = side * (5 + 9 * spread) + (1 - spread) * 5;
        const y = side === 0 ? 12 * (1 - spread) : 26 * spread;
        panel.style.setProperty('--fan-x', x.toFixed(2) + '%');
        panel.style.setProperty('--mobile-fan-x', mobileX.toFixed(2) + '%');
        panel.style.setProperty('--fan-r', rotation.toFixed(2) + 'deg');
        panel.style.setProperty('--fan-y', y.toFixed(2) + 'px');
        panel.style.setProperty('--fan-scale', (.86 + .14 * spread).toFixed(4));
      });
      scene.style.setProperty('--story-progress', progress.toFixed(4));
      scene.style.setProperty('--story-heading-y', (-18 * progress).toFixed(2) + 'px');
      scene.style.setProperty('--story-text-x', (-60 + progress * 120).toFixed(2) + 'px');
    }
    if (pointerDirty && activeCard?.isConnected) {
      activeCard.style.setProperty('--tilt-x', (-pointerY * 4 * intensity()).toFixed(2) + 'deg');
      activeCard.style.setProperty('--tilt-y', (pointerX * 5 * intensity()).toFixed(2) + 'deg');
      pointerDirty = false;
    }
    if (progress !== target) requestFrame();
  }
  function mount() {
    resetCard();
    scene = document.querySelector('.silk-story');
    panels = scene ? Array.from(scene.querySelectorAll('.silk-panel')) : [];
    hero = document.querySelector('.campaign-photo');
    editorial = document.querySelector('.editorial-motion .editorial-image');
    headerHeight = control('sticky_header',true) ? (document.querySelector('.header')?.offsetHeight || 80) : 0;
    progress = 0;
    target = 0;
    dirty = true;
    visibility?.disconnect();
    if ('IntersectionObserver' in window) {
      visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.target.classList.toggle('motion-out-of-view', !entry.isIntersecting));
      }, {rootMargin: '120px'});
      document.querySelectorAll('.campaign-hero, .colour-ribbon').forEach(el => visibility.observe(el));
    }
    applyPreference();
  }
  document.addEventListener('click', event => {
    if (event.target.closest('[data-motion-toggle]')) {
      if (reduced.matches || editing || !control('motion_enabled',true)) return;
      paused = !paused;
      try { localStorage.setItem('dipn-motion-paused', String(paused)); } catch {}
      applyPreference();
    }
    if (event.target.closest('[data-motion-scroll]')) {
      const story = document.querySelector('.silk-story');
      if (story) window.scrollTo({top: window.scrollY + story.getBoundingClientRect().top - headerHeight, behavior: disabled ? 'instant' : 'smooth'});
    }
  });
  document.addEventListener('pointermove', event => {
    if (disabled || !pointer.matches || !control('tilt_enabled',true)) return;
    const card = event.target.closest('.card-media');
    if (!card) { resetCard(); return; }
    if (card !== activeCard) {
      resetCard();
      activeCard = card;
      cardBounds = card.getBoundingClientRect();
    }
    pointerX = clamp((event.clientX - cardBounds.left) / cardBounds.width * 2 - 1, -1, 1);
    pointerY = clamp((event.clientY - cardBounds.top) / cardBounds.height * 2 - 1, -1, 1);
    pointerDirty = true;
    requestFrame();
  }, {passive: true});
  document.addEventListener('pointerout', event => {
    if (activeCard && !activeCard.contains(event.relatedTarget)) resetCard();
  }, {passive: true});
  window.addEventListener('scroll', () => { dirty = true; resetCard(); requestFrame(); }, {passive: true});
  window.addEventListener('resize', () => { headerHeight = control('sticky_header',true) ? (document.querySelector('.header')?.offsetHeight || 80) : 0; dirty = true; resetCard(); requestFrame(); }, {passive: true});
  document.addEventListener('visibilitychange', () => {
    root.classList.toggle('motion-background', document.hidden);
    if (document.hidden) { window.cancelAnimationFrame(frame); frame = 0; }
    else { dirty = true; requestFrame(); }
  });
  reduced.addEventListener('change', applyPreference);
  pointer.addEventListener('change', resetCard);
  window.DIPN_MOTION = {mount, get disabled() { return disabled; }};
  applyPreference();
})();
